/* eslint-disable */
import { BookOutlined, CloseOutlined, DownloadOutlined, EditOutlined, FileDoneOutlined, ImportOutlined, PrinterOutlined, SaveOutlined, WarningOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import {
  Button, Card, message, Modal, Space, Spin, Table, Tooltip, Typography
} from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextArea from 'antd/es/input/TextArea';
import { useAppSelector } from "app/store";
import Input from 'common/components/Input';
import SearchInput from 'common/components/PageTable/searchInput';
import { getAccessToken } from 'common/services/common/storage';
import { exportSalaryReportService } from "common/services/extends/payrollPeriods";
import { addNoteSupportAnotherService, createSalaryRecordsService } from 'common/services/extends/salaryRecords';
import { CreateSalaryRecordsParams, NotSupportAnotherParamsTypes } from 'common/services/extends/salaryRecords/types';
import { printStaffService } from 'common/services/extends/staff';
import { ROUTE_PATHS } from 'common/utils/constant';
import { downloadFile, formatDateDDMMYYYY, formatNumber } from 'common/utils/functions';
import roles, { getPermission } from "configs/roles";
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface CalculateSalaryDataType {
  id: number;
  key: React.Key;
  order: number;
  name: string;
  position: string;
  phoneNumber: string;
  taxCode: string;
  socialInsurance: string;
  socialInsuranceTime: string;
  identityNumber: string;
  idCardIssuedAt: string;
  idCardIssuedBy: string;
  bankNumber: string;
  bankName: string;
  bankBranch: string;
  province: string;
  workedShift: number;
  workedDay: number;
  unitPrice: number;
  totalPrice: number;
  training: number;
  workOnHoliday: number;
  survey: number;
  phoneAllowance: number;
  goFar: number;
  setup: number;
  stay: number;
  eat: number;
  ot: number;
  warehouse: number;
  amountReceived: number;
  salaryWarning: boolean;
  salaryWarningYear: boolean;
  supportAnother: number;
  note?: string;
}

interface FormValues {
  users: CalculateSalaryDataType[];
}

interface SalaryCalculatingProps {
  approved?: boolean;
  disableAction?: boolean;
}

const SalaryCalculating: React.FC<SalaryCalculatingProps> = ({
  approved,
  disableAction
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || '';
  const staffFullTimeId = Number(searchParams.get('staffFullTimeId'));
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  const { t } = useTranslation();
  const { roles: rolesUser } = useAppSelector((state) => state.auth);
  const [editStaff, setEditStaff] = useState<CalculateSalaryDataType | null>(null);
  const [indexEdit, setIndexEdit] = useState<number>(-1);
  const [isNoteOpen, setIsNoteOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const [jsonFile, setJsonFile] = useState<string>('');
  const [isLoadingJsonFile, setIsLoadingJsonFile] = useState<boolean>(false);
  const loadedJSonFileRef = useRef<boolean>(false);
  const usersRef = useRef<CalculateSalaryDataType[]>([]);

  const method = useForm<FormValues>({
    defaultValues: {
      users: [],
    },
  });

  const methodNote = useForm<{ note: string, id: number | null }>({
    defaultValues: {
      note: '',
      id: null
    },
    mode: 'onChange',
  });

  const streamEmployees = async (keyword?: string) => {
    try {
      setIsLoadingJsonFile(true);
      const token = getAccessToken();
      // request with form data to api for endpoint /api/v1/staffs/import
      const res = await fetch(`/api/v1/salary-records?_payrollPeriodId=${payrollPeriodId}&keyword=${keyword || ''}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.log('Fail with status: ', res?.status);
        if (res.status === 401) {
          navigate('/login');
        } else {
          message.error(t('message.uploadError'));
        }
        setIsLoadingJsonFile(false);
        return;
      }
      const reader = res.body?.getReader();

      let result;
      const decoder = new TextDecoder();
      if (reader) {
        do {
          // eslint-disable-next-line no-await-in-loop
          result = await reader.read();
          const record = decoder.decode(result.value, { stream: true });
          setJsonFile((prev) => prev + record);
        } while (!result.done);

        if (result.done) {
          loadedJSonFileRef.current = true;
          setIsLoadingJsonFile(false);
        }
      }
    } catch (e) {
      setIsLoadingJsonFile(false);
      // Emtry
    }
  };

  useEffect(() => {
    if (!getPermission(rolesUser, roles.SALARY_RECORD_INDEX)) return;

    if (!!payrollPeriodId) {
      streamEmployees();
    }
  }, [rolesUser]);

  useEffect(() => {
    if (!loadedJSonFileRef.current) {
      method.reset({
        users: [],
      });
      return;
    }
    if (jsonFile === '') {
      method.reset({
        users: [],
      });
      return;
    }

    const lines = jsonFile.split('\n');
    const rowData: FormValues = { users: [] };

    lines.forEach((line) => {
      if (line.trim()) {
        const json = JSON.parse(line);
        if (json.status === 'processing') {
          const { staffData, position, bank, salaryData } = json.data;
          rowData.users.push({
            id: staffData.id,
            key: staffData.id,
            name: staffData.fullname,
            identityNumber: staffData.idCardNumber,
            position: position?.name || '',
            phoneNumber: staffData.phoneNumber,
            taxCode: staffData.taxCode,
            socialInsurance: staffData.socialInsuranceNumber,
            socialInsuranceTime: staffData.socialInsuranceJoinDate,
            bankBranch: bank?.branchName || '',
            bankName: bank?.bankName || '',
            bankNumber: bank?.accountNumber || '',
            amountReceived: salaryData.income,
            eat: salaryData.eat,
            idCardIssuedAt: staffData.idCardIssuedAt,
            idCardIssuedBy: staffData.idCardIssuedBy,
            goFar: salaryData.goFar,
            order: 0,
            ot: salaryData.ot,
            phoneAllowance: salaryData.phoneAllowance,
            province: bank?.province || '',
            setup: salaryData.setup,
            stay: salaryData.stay,
            warehouse: salaryData.warehouse,
            workedShift: staffData.totalShiftCount,
            workedDay: staffData.totalWorkingDay || 0,
            totalPrice: staffData.totalSalary,
            survey: salaryData.survey,
            training: salaryData.training,
            unitPrice: salaryData.baseSalary,
            workOnHoliday: salaryData.workOnHoliday,
            salaryWarning: staffData.salaryWarning,
            salaryWarningYear: staffData.salaryWarningYear,
            supportAnother: salaryData.supportAnother,
            note: salaryData.note,
          });
        }
      }
    });

    usersRef.current = rowData.users;
    method.reset(rowData);

  }, [jsonFile, loadedJSonFileRef.current]);

  // search with keyword from usersRef
  useEffect(() => {
    if (keyword === '') {
      method.reset({
        users: usersRef.current,
      });
      return;
    }
    const filtered = usersRef.current.filter((user) => user.name.toLowerCase().includes(keyword.toLowerCase()));
    method.reset({
      users: filtered,
    });
  }, [keyword]);

  const { mutate: saveSalaryRecordByStaffMutate, isLoading: isLoadingSaveSalaryRecord } = useMutation(
    ['save-salary-record-by-staff'],
    (params: CreateSalaryRecordsParams) => createSalaryRecordsService(params),
    {
      onSuccess(res, variables) {
        const totalPrice = (editStaff?.workedShift || 0) * variables.baseSalary;
        const amountReceived = totalPrice
          + (variables.training || 0)
          + (variables.workOnHoliday || 0)
          + (variables.survey || 0)
          + (variables.phoneAllowance || 0)
          + (variables.goFar || 0)
          + (variables.setup || 0)
          + (variables.stay || 0)
          + (variables.eat || 0)
          + (variables.ot || 0)
          + (variables.warehouse || 0)
          + (variables.supportAnother || 0);
        method.setValue(`users.${indexEdit}.unitPrice`, variables.baseSalary);
        method.setValue(`users.${indexEdit}.totalPrice`, totalPrice);
        method.setValue(`users.${indexEdit}.training`, variables.training);
        method.setValue(`users.${indexEdit}.workOnHoliday`, variables.workOnHoliday);
        method.setValue(`users.${indexEdit}.survey`, variables.survey);
        method.setValue(`users.${indexEdit}.phoneAllowance`, variables.phoneAllowance);
        method.setValue(`users.${indexEdit}.goFar`, variables.goFar);
        method.setValue(`users.${indexEdit}.setup`, variables.setup);
        method.setValue(`users.${indexEdit}.stay`, variables.stay);
        method.setValue(`users.${indexEdit}.eat`, variables.eat);
        method.setValue(`users.${indexEdit}.ot`, variables.ot);
        method.setValue(`users.${indexEdit}.warehouse`, variables.warehouse);
        method.setValue(`users.${indexEdit}.amountReceived`, amountReceived);
        method.setValue(`users.${indexEdit}.salaryWarning`, res.salaryWarning);
        method.setValue(`users.${indexEdit}.salaryWarningYear`, res.salaryWarningYear);
        method.setValue(`users.${indexEdit}.supportAnother`, variables.supportAnother || 0);

        // update usersRef
        const users = method.getValues('users');
        usersRef.current = users.map((user, index) => {
          if (index === indexEdit) {
            return {
              ...user,
              unitPrice: variables.baseSalary,
              totalPrice,
              training: variables.training,
              workOnHoliday: variables.workOnHoliday,
              survey: variables.survey,
              phoneAllowance: variables.phoneAllowance,
              goFar: variables.goFar,
              setup: variables.setup,
              stay: variables.stay,
              eat: variables.eat,
              ot: variables.ot,
              warehouse: variables.warehouse,
              amountReceived,
              salaryWarning: res.salaryWarning,
              salaryWarningYear: res.salaryWarningYear,
              supportAnother: variables.supportAnother || 0
            }
          }
          return user;
        });

        // setTotalAmountSalary(method.getValues('users').reduce((prev, current) => prev + current.amountReceived, 0));
        message.success(t('message.updateSuccess'));
      },
      onError() {
        message.error(t('message.updateError'));
      },
      onSettled() {
        setEditStaff(null);
        setIndexEdit(-1);
      }
    }
  );

  const { mutate: downloadMutate, isLoading: isLoadingDownload } = useMutation(
    ['download-salary-report'],
    () => exportSalaryReportService(projectId, payrollPeriodId),
    {
      onSuccess: (res) => {
        if (res.link) {
          downloadFile(res.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError() {
        message.error(t('message.exportError'));
      }
    }
  );


  const { mutate: addNoteSupportAnotherMutate, isLoading: addNoteSupportAnotherLoading } = useMutation(
    ['addNoteSupportAnother'],
    async (params: {
      id: string,
      params: NotSupportAnotherParamsTypes,
    }) => addNoteSupportAnotherService(params.id, params.params),
    {
      onSuccess: (_, variables) => {
        method.setValue(`users.${indexEdit}.note`, variables.params.note);
        message.success(t('message.updateSuccess'));
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
      onSettled: () => {
        setIsNoteOpen(false);
        if (!editStaff) {
          setIndexEdit(-1);
        }
      }
    }
  );

  const updateField = (field: any, valueParams?: string) => {
    const clone = { ...editStaff } as any;
    if (typeof valueParams === 'object') {
      clone[field] = valueParams;
    } else {
      clone[field] = valueParams ? Number(valueParams) : undefined;
    }
    setEditStaff(clone);
  };

  const handleSubmitNote = async () => {
    const isTigger = await methodNote.trigger();
    if (!isTigger) return;
    const formData = methodNote.getValues();
    if (payrollPeriodId && formData.id) {
      addNoteSupportAnotherMutate({
        id: payrollPeriodId,
        params: {
          note: formData.note,
          staffId: formData.id
        }
      })
    }
  };

  const { mutate: printMuate, isLoading: isLoadingPrint } = useMutation(
    ['print-employee'],
    (id: number) => printStaffService(id),
    {
      onSuccess: (data) => {
        window.open(data.link, '_blank');
        message.success(t('message.exportSuccess'));
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const onHandlePrint = (id: number) => {
    printMuate(id);
  };

  const actionColumn: TableColumnsType<CalculateSalaryDataType> = useMemo(() => {
    if (!approved) {
      return [
        {
          title: t('system.action'),
          key: 'action',
          width: editStaff ? 100 : 40,
          align: 'center',
          fixed: 'right',
          render: (data: CalculateSalaryDataType, _record: any, rowIndex: number) => (
            <Space>
              {
                editStaff && editStaff.id === data.id ? (
                  <>
                    <Tooltip title="Lưu">
                      <Button
                        icon={<SaveOutlined />}
                        onClick={() => {
                          if (payrollPeriodId) {
                            saveSalaryRecordByStaffMutate({
                              payrollPeriodId: Number(payrollPeriodId),
                              staffId: _record.id,
                              baseSalary: editStaff.unitPrice || 0,
                              eat: editStaff.eat || 0,
                              goFar: editStaff.goFar || 0,
                              ot: editStaff.ot || 0,
                              phoneAllowance: editStaff.phoneAllowance || 0,
                              setup: editStaff.setup || 0,
                              stay: editStaff.stay || 0,
                              survey: editStaff.survey || 0,
                              training: editStaff.training || 0,
                              warehouse: editStaff.warehouse || 0,
                              workOnHoliday: editStaff.workOnHoliday || 0,
                              supportAnother: editStaff.supportAnother || 0,
                            });
                          }
                        }}
                        disabled={isLoadingSaveSalaryRecord || !getPermission(rolesUser, roles.SALARY_RECORD_STORE) || disableAction}
                      >
                        Lưu
                      </Button>
                    </Tooltip>
                    <Tooltip title="Hủy">
                      <Button
                        icon={<CloseOutlined />}
                        onClick={() => setEditStaff(null)}
                      >
                        Hủy
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title="Chỉnh sửa">
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditStaff(data);
                        setIndexEdit(rowIndex);
                      }}
                      disabled={!!editStaff || !getPermission(rolesUser, roles.SALARY_RECORD_STORE) || disableAction}
                    />
                  </Tooltip>
                )
              }
            </Space>
          ),
        }
      ]
    }
    return [];
  }, [editStaff, isLoadingSaveSalaryRecord, approved, rolesUser, payrollPeriodId, disableAction]);

  const columns: TableColumnsType<CalculateSalaryDataType> = useMemo(() => [
    {
      title: 'STT',
      dataIndex: 'key',
      fixed: 'left',
      width: 30,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: t('employee.printContract'),
      dataIndex: 'printContract',
      key: 'printContract',
      fixed: 'left',
      width: 60,
      align: 'center',
      render: (_value: any, _record) => {
        return (
          <Tooltip title={t('employee.printContract')}>
            <Button
              disabled={!getPermission(rolesUser, roles.PROJECT_PRINT_CONTRACT) || isLoadingPrint}
              icon={<PrinterOutlined />}
              onClick={() => onHandlePrint(_record.id)}
            />
          </Tooltip>
        )
      }
    },
    {
      title: 'Họ và tên',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 68,
      render: (_value: any, _record) => {
        return (
          <Space>
            {_value}
            {
              (_record.salaryWarning || _record.salaryWarningYear) && (
                <Tooltip
                  title={<div dangerouslySetInnerHTML={{
                    __html: `
                      <div>${t('salary.didYou')}</div>
                      <ul class="salary_warning-ul">
                        ${_record.salaryWarning ? `<li>${t('salary.exceedingTheMonthlySalaryThreshold')}</li>` : ''}
                        ${_record.salaryWarningYear ? `<li>${t('salary.exceedingTheAnnualSalaryThreshold')}</li>` : ''}
                      </ul>
                `}} />}
                >
                  <WarningOutlined size={30} style={{ color: "#ff3b30" }} />
                </Tooltip>
              )
            }
          </Space>
        )
      }
    },
    {
      title: 'Vị trí',
      dataIndex: 'position',
      key: 'position',
      width: 35,
      align: 'center',
    },
    {
      title: 'Thông tin nhân viên',
      children: [
        {
          title: 'Số ĐT',
          dataIndex: 'phoneNumber',
          key: 'phoneNumber',
          width: 60,
          align: 'center',
        },
        {
          title: 'Số BHXH',
          dataIndex: 'socialInsurance',
          key: 'socialInsurance',
          width: 60,
          align: 'center',
        },
        {
          title: (
            <div>
              Thời gian
              <br />
              đóng BHXH
            </div>
          ),
          dataIndex: 'socialInsuranceTime',
          key: 'socialInsuranceTime',
          width: 65,
          align: 'center',
          render: (_value) => formatDateDDMMYYYY(_value, '/')
        },
        {
          title: 'MST',
          dataIndex: 'taxCode',
          key: 'taxCode',
          width: 60,
          align: 'center',
        },
        {
          title: 'CCCD',
          dataIndex: 'identityNumber',
          key: 'identityNumber',
          width: 65,
          align: 'center',
        },
        {
          title: 'Ngày cấp',
          dataIndex: 'idCardIssuedAt',
          key: 'idCardIssuedAt',
          width: 65,
          align: 'center',
          render: (_value) => formatDateDDMMYYYY(_value, '/')
        },
        {
          title: 'Nơi cấp',
          dataIndex: 'idCardIssuedBy',
          key: 'idCardIssuedBy',
          width: 60,
          align: 'center',
        },
      ],
    },
    {
      title: 'Thông tin chuyển khoản',
      children: [
        {
          title: 'Số tài khoản',
          dataIndex: 'bankNumber',
          key: 'bankNumber',
          width: 100,
          align: 'center',
          render: (bankNumber: any) => bankNumber
        },
        {
          title: 'Ngân hàng',
          dataIndex: 'bankName',
          key: 'bankName',
          width: 100,
          align: 'center',
          render: (bankName: any) => bankName
        },
        {
          title: 'Chi nhánh',
          dataIndex: 'bankBranch',
          key: 'bankBranch',
          width: 100,
          align: 'center',
          render: (bankBranch: any) => bankBranch
        },
        // {
        //   title: 'Tỉnh',
        //   dataIndex: 'province',
        //   key: 'province',
        //   width: 130,
        // },
      ],
    },
    {
      title: 'Số ca',
      dataIndex: 'workedShift',
      key: 'workedShift',
      width: 40,
      align: 'center'
    },
    {
      title: 'Ngày công',
      dataIndex: 'workedDay',
      key: 'workedDay',
      align: 'center',
      width: 55,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: editStaff ? 65 : 40,
      align: 'center',
      render: (_value: any, _record) => {
        if (editStaff && editStaff.id === _record?.id) {
          return (
            <Input
              value={editStaff.unitPrice}
              onChange={(val) => updateField('unitPrice', val.target.value)}
              type="number"
              hasComma
            />
          )
        }
        return formatNumber(_value)
      }
    },
    {
      title: 'Thành tiền',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: editStaff ? 65 : 50,
      align: 'center',
      render: (_value: any, _record) => {
        if (editStaff && editStaff.id === _record?.id) {
          const thanhTien = editStaff.workedShift * editStaff.unitPrice;
          return formatNumber(thanhTien)
        }
        return formatNumber(_value)
      }
    },
    {
      title: 'Hỗ trợ',
      children: [
        {
          title: 'Training',
          dataIndex: 'training',
          key: 'training',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.training}
                  onChange={(val) => updateField('training', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Làm ngày lễ',
          dataIndex: 'workOnHoliday',
          key: 'workOnHoliday',
          width: editStaff ? 65 : 50,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.workOnHoliday}
                  onChange={(val) => updateField('workOnHoliday', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Khảo Sát',
          dataIndex: 'survey',
          key: 'survey',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.survey}
                  onChange={(val) => updateField('survey', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Hỗ trợ ĐT',
          dataIndex: 'phoneAllowance',
          key: 'phoneAllowance',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.phoneAllowance}
                  onChange={(val) => updateField('phoneAllowance', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Đi xa',
          dataIndex: 'goFar',
          key: 'goFar',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.goFar}
                  onChange={(val) => updateField('goFar', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Setup',
          dataIndex: 'setup',
          key: 'setup',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.setup}
                  onChange={(val) => updateField('setup', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Ở',
          dataIndex: 'stay',
          key: 'stay',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.stay}
                  onChange={(val) => updateField('stay', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Ăn',
          dataIndex: 'eat',
          key: 'eat',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.eat}
                  onChange={(val) => updateField('eat', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Thêm giờ',
          dataIndex: 'ot',
          key: 'ot',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.ot}
                  onChange={(val) => updateField('ot', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Kho',
          dataIndex: 'warehouse',
          key: 'warehouse',
          width: editStaff ? 65 : 40,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.warehouse}
                  onChange={(val) => updateField('warehouse', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: 'Hỗ trợ khác',
          dataIndex: 'supportAnother',
          key: 'supportAnother',
          width: editStaff ? 65 : 55,
          align: 'center',
          render: (_value: any, _record) => {
            if (editStaff && editStaff.id === _record?.id) {
              return (
                <Input
                  value={editStaff.supportAnother}
                  onChange={(val) => updateField('supportAnother', val.target.value)}
                  type="number"
                  hasComma
                />
              )
            }
            return formatNumber(_value)
          }
        },
        {
          title: <div dangerouslySetInnerHTML={{ __html: t('salary.otherNote') }} />,
          width: 70,
          key: 'otherNote',
          align: 'center',
          render: (_value: string, _record, index) => (
            <Space>
              <Tooltip title={t('salary.note')}>
                <Button
                  icon={_record.note ? <FileDoneOutlined style={{ color: '#f69320' }} /> : <BookOutlined />}
                  onClick={() => {
                    setIndexEdit(index);
                    setIsNoteOpen(true);
                    methodNote.reset({
                      note: _record.note || '',
                      id: _record.id
                    });
                  }}
                />
              </Tooltip>
            </Space>
          ),
        },
      ],
    },
    {
      title: 'Thực nhận',
      dataIndex: 'amountReceived',
      key: 'amountReceived',
      width: editStaff ? 60 : 50,
      align: 'center',
      fixed: 'right',
      render: (_value: any, _record) => {
        if (editStaff && editStaff.id === _record?.id) {
          const totalPrice = editStaff.workedShift * editStaff.unitPrice;
          const amountReceived = totalPrice
            + (editStaff.training || 0)
            + (editStaff.workOnHoliday || 0)
            + (editStaff.survey || 0)
            + (editStaff.phoneAllowance || 0)
            + (editStaff.goFar || 0)
            + (editStaff.setup || 0)
            + (editStaff.stay || 0)
            + (editStaff.eat || 0)
            + (editStaff.ot || 0)
            + (editStaff.warehouse || 0)
            + (editStaff.supportAnother || 0);
          return formatNumber(amountReceived)
        }
        return formatNumber(_value)
      }
    },
    ...actionColumn
  ], [editStaff, actionColumn, t, isLoadingPrint]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    setSearchParams(newSearchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      loading={isLoadingJsonFile}
      title={(
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="time-keeping-table__header-left">
            <Typography.Title level={4}>
              {t('salary.tableSalary')}
            </Typography.Title>
          </div>
          <div style={{ marginLeft: 16 }}>
            <SearchInput
              handleSearch={setKeyword}
            />
          </div>
        </div>
      )}
      extra={(
        <Space>
          {
            !approved && (
              <>
                <Button
                  size="middle"
                  type="default"
                  icon={(<DownloadOutlined />)}
                  loading={isLoadingDownload}
                  disabled={!getPermission(rolesUser, roles.IMPORT_SALARY_REPORT) || disableAction}
                  onClick={() => downloadMutate()}
                >
                  {t('salary.download')}
                </Button>
                <Button
                  size="middle"
                  type="default"
                  icon={(<ImportOutlined />)}
                  disabled={!getPermission(rolesUser, roles.IMPORT_SALARY_REPORT) || disableAction}
                  onClick={() => navigate(`${ROUTE_PATHS.SALARY_CALCULATE_IMPORT}?payrollPeriodId=${payrollPeriodId}&staffFullTimeId=${staffFullTimeId}&projectId=${projectId}`)}
                >
                  {t('salary.import')}
                </Button>
              </>
            )
          }

          <Button
            size="middle"
            style={{ borderColor: '#34C759', color: '#34C759' }}
            onClick={() => navigate(`${ROUTE_PATHS.TIMEKEEPING_DETAIL}?projectId=${projectId}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`)}
          >
            {t('salary.adjustTimeKeeping')}
          </Button>
        </Space>
      )}
    >
      <Spin spinning={isLoadingSaveSalaryRecord}>
        <Table<CalculateSalaryDataType>
          columns={columns}
          dataSource={method.getValues('users')}
          bordered
          scroll={{ x: 4200, y: 60 * 15 }}
          pagination={false}
          rowClassName={(row) => {
            return `${row.salaryWarning || row.salaryWarningYear ? 'salary_warning ' : ''}salary_row`;
          }}
          summary={(records) => {

            const summaryFieldsNumber = [
              'workedShift',
              'workedDay',
            ];

            const summaryFieldsAmount = [
              'unitPrice',
              'totalPrice',
              'training',
              'workOnHoliday',
              'survey',
              'phoneAllowance',
              'goFar',
              'setup',
              'stay',
              'eat',
              'ot',
              'warehouse',
              'supportAnother',
              'note',
              'amountReceived'
            ];

            const summaryValuesNumber: Record<string, number> = summaryFieldsNumber.reduce((acc, field) => {
              acc[field] = records.reduce((sum, item) => {
                const value = Number((item as any)[field]);
                return sum + (isNaN(value) ? 0 : value);
              }, 0);
              return acc;
            }, {} as Record<string, number>);

            const summaryValuesAmmount: Record<string, number> = summaryFieldsAmount.reduce((acc, field) => {
              acc[field] = records.reduce((sum, item) => {
                const value = Number((item as any)[field]);
                return sum + (isNaN(value) ? 0 : value);
              }, 0);
              return acc;
            }, {} as Record<string, number>);

            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                      Tổng:
                    </Typography.Text>
                  </Table.Summary.Cell>
                  {
                    Array(11).fill(null).map((_, index) => (
                      <Table.Summary.Cell index={3 + index} />
                    ))
                  }
                  {
                    summaryFieldsNumber.map((field, index) => (
                      <Table.Summary.Cell index={14 + index} align="center">
                        <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {summaryValuesNumber[field]}
                        </Typography.Text>
                      </Table.Summary.Cell>
                    ))
                  }
                  {
                    summaryFieldsAmount.map((field, index) => (
                      <Table.Summary.Cell index={16 + index} align="center">
                        <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                          {formatNumber(summaryValuesAmmount[field])}
                        </Typography.Text>
                      </Table.Summary.Cell>
                    ))
                  }
                  <Table.Summary.Cell align="center" index={summaryFieldsNumber.concat(summaryFieldsAmount).length + 9 + 1 + 5} />
                </Table.Summary.Row>
              </Table.Summary>
            )
          }}
        />
      </Spin>
      <Modal
        open={isNoteOpen}
        onCancel={() => {
          setIsNoteOpen(false);
          methodNote.reset({
            note: '',
            id: null
          });
        }}
        okButtonProps={{
          disabled: approved || disableAction || addNoteSupportAnotherLoading || !getPermission(rolesUser, roles.SALARY_RECORD_NOTE_SUPPORT_ANOTHER)
        }}
        cancelButtonProps={{
          disabled: approved || disableAction || !getPermission(rolesUser, roles.SALARY_RECORD_NOTE_SUPPORT_ANOTHER)
        }}
        onOk={() => handleSubmitNote()}
        width={800}
        title={(
          <Typography.Title
            level={4}
          >
            {t('salary.otherNotePopup')}
          </Typography.Title>
        )}
      >
        <FormProvider {...methodNote}>
          <Controller
            name="note"
            render={({
              field: { value, onChange }, fieldState: { error }
            }) => (
              <>
                <TextArea
                  className="u-mt-8"
                  value={value}
                  onChange={onChange}
                  size="large"
                  rows={6}
                  style={{ minHeight: 50 }}
                  readOnly={approved}
                />
                {error && (
                  <span
                    className="a-input_errorMessage"
                  >
                    {error.message}
                  </span>
                )}
              </>
            )}
          />
        </FormProvider>
      </Modal>
    </Card>
  );
};

export default SalaryCalculating;
